/* ------------------------------------------------------------------------------------------------------------------
   wave-motion.js

   Comportamiento de las tres piezas de movimiento de las secciones de texto largo. Se inicializa solo sobre cualquier
   elemento con [data-wave-motion] y lee su configuración de los data-attributes que escribe el snippet
   'wave-motion.liquid', de modo que apagar un ajuste en el editor apaga también su trabajo aquí.

   Sin dependencias. Un único bucle de scroll compartido por todas las secciones de la página.
   ------------------------------------------------------------------------------------------------------------------ */

(function () {
  'use strict';

  var WIDTH = 2880; // el doble del viewBox: deja margen para que la deriva cierre el ciclo
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var instances = [];
  var ticking = false;

  /* Perfiles del corte. El armónico secundario (amp2) es lo que convierte una sinusoide limpia en espuma. */
  var PROFILES = {
    soft: { len: 1400, amp2: 0, len2: 0 },
    crest: { len: 760, amp2: 0, len2: 0 },
    foam: { len: 900, amp2: 0.45, len2: 210 }
  };

  /* Una onda como path: amplitud, largo de cresta y fase son parámetros, no números incrustados a mano. */
  function wavePath(options) {
    var d = '';
    var x;
    var y;

    for (x = 0; x <= options.width; x += 7) {
      y = options.base + options.amp * Math.sin((x / options.len) * Math.PI * 2 + options.phase);

      if (options.amp2) {
        y += options.amp2 * Math.sin((x / options.len2) * Math.PI * 2 + options.phase * 1.7);
      }

      d += (x === 0 ? 'M' : 'L') + x.toFixed(1) + ' ' + y.toFixed(1);
    }

    if (typeof options.fillTo === 'number') {
      d += 'L' + options.width + ' ' + options.fillTo + 'L0 ' + options.fillTo + 'Z';
    }

    return d;
  }

  function readNumber(el, name, fallback) {
    var value = parseFloat(el.getAttribute(name));
    return isNaN(value) ? fallback : value;
  }

  /* --- Marea de fondo --------------------------------------------------------------------------------------------- */

  function paintTide(root) {
    var band = root.querySelector('.wave-motion__band');
    if (!band) return;

    var strength = readNumber(root, 'data-wave-strength', 60) / 100;
    var layers = [
      { selector: '.wave-motion__wave--back', amp: 30, len: 980, phase: 0.3, base: 120, opacity: 0.06 * strength + 0.015 },
      { selector: '.wave-motion__wave--mid', amp: 22, len: 700, phase: 2.2, base: 176, opacity: 0.1 * strength + 0.02 },
      { selector: '.wave-motion__wave--front', amp: 15, len: 460, phase: 4.1, base: 232, opacity: 0.16 * strength + 0.03 }
    ];

    layers.forEach(function (layer) {
      var path = band.querySelector(layer.selector);
      if (!path) return;

      path.setAttribute('d', wavePath({
        width: WIDTH,
        amp: layer.amp,
        len: layer.len,
        phase: layer.phase,
        base: layer.base,
        fillTo: 320
      }));
      path.setAttribute('fill-opacity', layer.opacity.toFixed(3));
    });
  }

  /* --- Corte de ola ----------------------------------------------------------------------------------------------- */

  function cutMask(root) {
    var profile = PROFILES[root.getAttribute('data-wave-cut-profile')] || PROFILES.soft;
    var amp = readNumber(root, 'data-wave-cut-amplitude', 18);
    var bothEdges = root.getAttribute('data-wave-cut-edges') !== 'bottom';
    var bottomY = 600 - amp - 4;
    var bottomLen = profile.len * 1.08;
    var top;
    var x;
    var y;

    if (bothEdges) {
      top = wavePath({
        width: WIDTH,
        amp: amp,
        len: profile.len,
        phase: 1.1,
        base: amp + 4,
        amp2: profile.amp2 * amp,
        len2: profile.len2
      });
    } else {
      top = 'M0 0L' + WIDTH + ' 0';
    }

    /* La cresta inferior se recorre de derecha a izquierda para cerrar el área de la máscara. */
    var bottom = '';
    for (x = WIDTH; x >= 0; x -= 7) {
      y = bottomY + amp * Math.sin((x / bottomLen) * Math.PI * 2 + 2.7);

      if (profile.amp2) {
        y += profile.amp2 * amp * Math.sin((x / profile.len2) * Math.PI * 2 + 2.7 * 1.7);
      }

      bottom += 'L' + x.toFixed(1) + ' ' + y.toFixed(1);
    }

    var svg =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + WIDTH + ' 600" preserveAspectRatio="none">' +
      '<path d="' + top + bottom + 'Z" fill="#fff"/></svg>';

    return 'url("data:image/svg+xml,' + encodeURIComponent(svg) + '")';
  }

  function paintCut(root) {
    root.style.setProperty('--wave-cut-mask', cutMask(root));
  }

  /* --- Revelado por párrafo --------------------------------------------------------------------------------------- */

  var observer = null;

  function getObserver() {
    if (observer || !('IntersectionObserver' in window)) return observer;

    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) entry.target.classList.add('is-lit');
        });
      },
      { rootMargin: '0px 0px -16% 0px', threshold: 0.12 }
    );

    return observer;
  }

  function setupReveal(instance) {
    var selector = instance.root.getAttribute('data-wave-text') || 'p';
    var nodes = instance.root.querySelectorAll(selector);

    instance.paragraphs = Array.prototype.slice.call(nodes);
    instance.paragraphs.forEach(function (p) {
      p.classList.add('wave-motion-text');

      /* Lo que ya está en pantalla al cargar arranca encendido: la primera vista se lee entera y el revelado queda
         para lo que entra después, que es donde el gesto significa algo. */
      if (reduced || p.getBoundingClientRect().top < window.innerHeight * 0.92) {
        p.classList.add('is-lit');
      }
    });

    var io = getObserver();

    if (io && !reduced) {
      instance.paragraphs.forEach(function (p) {
        io.observe(p);
      });
    } else {
      instance.paragraphs.forEach(function (p) {
        p.classList.add('is-lit');
      });
    }
  }

  /* --- Bucle de scroll -------------------------------------------------------------------------------------------- */

  function onFrame() {
    var viewport = window.innerHeight;

    instances.forEach(function (instance) {
      var root = instance.root;
      var rect = root.getBoundingClientRect();
      var visible = rect.bottom > -200 && rect.top < viewport + 200;

      if (instance.parallax && visible) {
        instance.images.forEach(function (image) {
          var box = image.getBoundingClientRect();
          if (box.bottom < -200 || box.top > viewport + 200) return;

          var center = (box.top + box.height / 2 - viewport / 2) / viewport;
          image.style.setProperty('--wave-parallax-offset', (center * -26).toFixed(1) + 'px');
        });
      }

      if (instance.focusMode && visible && instance.paragraphs.length) {
        var best = null;
        var bestDistance = Infinity;

        instance.paragraphs.forEach(function (p) {
          var box = p.getBoundingClientRect();
          var distance = Math.abs(box.top + box.height / 2 - viewport * 0.42);

          if (distance < bestDistance) {
            bestDistance = distance;
            best = p;
          }
        });

        instance.paragraphs.forEach(function (p) {
          p.classList.toggle('is-focus', p === best);
        });
      }

      if (instance.rise) {
        var span = rect.height - viewport;
        var seen = span > 0 ? Math.min(1, Math.max(0, -rect.top / span)) : 1;
        root.style.setProperty('--wave-height', (16 + seen * 62).toFixed(1) + 'vh');
      }
    });

    ticking = false;
  }

  function requestFrame() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(onFrame);
  }

  /* --- Arranque -------------------------------------------------------------------------------------------------- */

  function init(root) {
    if (root.dataset.waveMotionReady === 'true') return;
    root.dataset.waveMotionReady = 'true';

    var instance = {
      root: root,
      images: [],
      paragraphs: [],
      parallax: root.getAttribute('data-wave-parallax') === 'true' && !reduced,
      focusMode: root.getAttribute('data-wave-reveal') === 'true' && root.getAttribute('data-wave-reveal-mode') === 'focus' && !reduced,
      rise: root.getAttribute('data-wave-rise') === 'true' && root.getAttribute('data-wave-tide') === 'true' && !reduced
    };

    if (root.getAttribute('data-wave-tide') === 'true') {
      paintTide(root);
    }

    if (root.getAttribute('data-wave-cut') === 'true') {
      var imageSelector = root.getAttribute('data-wave-images') || 'img';
      instance.images = Array.prototype.slice.call(root.querySelectorAll(imageSelector));
      instance.images.forEach(function (image) {
        image.classList.add('wave-motion-image');
      });
      paintCut(root);
    }

    if (root.getAttribute('data-wave-reveal') === 'true') {
      setupReveal(instance);
    }

    instances.push(instance);
    requestFrame();
  }

  function initAll(scope) {
    var container = scope || document;
    var nodes = container.querySelectorAll('[data-wave-motion]');
    Array.prototype.slice.call(nodes).forEach(init);
  }

  function teardown(scope) {
    instances = instances.filter(function (instance) {
      return !scope || !scope.contains(instance.root);
    });
  }

  window.addEventListener('scroll', requestFrame, { passive: true });
  window.addEventListener('resize', function () {
    instances.forEach(function (instance) {
      if (instance.root.getAttribute('data-wave-cut') === 'true') paintCut(instance.root);
    });
    requestFrame();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  /* El editor de temas recarga secciones sueltas: hay que reengancharlas y soltar las que se van. */
  document.addEventListener('shopify:section:load', function (event) {
    teardown(event.target);
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    teardown(event.target);
  });
})();
